/// <amd-module name="Repos/Tfvc/Views/FilesHub/Components/FilesHub" />
import * as React from "react";
import { DiffViewerOrientation } from "Repos/Common/Contracts/VersionControlViewModel";
import { FileExplorerTree } from "Repos/FilesHubCommon/Components/FileExplorerTree";
import { ReposMessages } from "Repos/Messages/Components/ReposMessages";
import * as Resources from "Repos/Tfvc/Views/FilesHub/Resources";
import { IOptionsViewService } from "Repos/Views/Settings/Options/Services/OptionsViewService";
import { getTFSProject } from "Tfs/Platform/Page";
import { ObservableValue } from "VSS/Core/Observable";
import { ScreenSize } from "VSS/Core/Util/Screen";
import { PerformanceScenario } from "VSS/Platform/Components/Performance";
import { IVssPageContext } from "VSS/Platform/Context";
import { Components, css, IVssLayoutManager, VssComponent } from "VSS/Platform/Layout";
import { tryGetLocalStorageValue, trySetLocalStorageValue } from "VSS/Platform/Util/Storage";
import { Observer } from "VSSUI/Observer";
import { Page } from "VSSUI/Page";
import { Splitter, SplitterElementPosition } from "VSSUI/Splitter";
import { Surface, SurfaceBackground } from "VSSUI/Surface";
import { TabBar, TabContent } from "VSSUI/Tabs";
import { ScreenSizeObserver } from "VSSUI/Utilities/ScreenSize";
import { getFilesHubTabsService, getFilesHubViewService } from "../FilesHubViewService";
import { FilesHeader } from "./FilesHeader";

const splitterSettingsKey = "Repos.FilesHub.SplitterWidth";

class FilesHub extends VssComponent<{}> {
    public render() {
        return (
            <PerformanceScenario area="Repos" scenarioName="tfvc-files-hub" tti={true}>
                <FilesHubContent pageContext={this.context.pageContext} />
            </PerformanceScenario>
        );
    }
}

const FilesHubContent = (props: { pageContext: IVssPageContext }): JSX.Element => {
    const { pageContext } = props;

    const filesService = getFilesHubViewService(pageContext);
    const layoutManager = pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
    const optionsSvc = pageContext.getService<IOptionsViewService>("OptionsViewService");
    const project = getTFSProject(pageContext);
    const tabsService = getFilesHubTabsService(pageContext);
    const webEditingEnabled = new ObservableValue<boolean>(false);
    if (project) {
        optionsSvc.getRepositoryOptions(project?.id, undefined, true).then(options => {
            webEditingEnabled.value = options[0].value;
        });
    } else {
        webEditingEnabled.value = true;
    }

    const onSelectedTabChanged = (newTabId: string) => {
        tabsService.setSelectedTabId(newTabId);
    };

    const onRenderNearElement = () => {
        return <FileExplorerTree filesService={filesService} webEditingEnabled={webEditingEnabled} />;
    };

    const onRenderFarElement = () => {
        const tabs = tabsService.getTabs();
        const selectedTabId = tabsService.getSelectedTabId();

        return (
            <Page className="repos-files-hub-page flex-grow custom-scrollbar scroll-auto-hide" tabProviders={tabs} selectedTabId={selectedTabId}>
                <FilesHeader pageContext={pageContext} filesService={filesService} webEditingEnabled={webEditingEnabled} />
                <TabBar selectedTabId={selectedTabId} onSelectedTabChanged={onSelectedTabChanged} />
                <div className={css("page-content page-content-top flex-column flex-noshrink flex-grow rhythm-vertical-16")}>
                    <ReposMessages className="flex-noshrink" showPullRequestSuggestions={false} />
                    <TabContent />
                </div>
            </Page>
        );
    };

    return (
        <Observer fullScreenMode={layoutManager.getFullScreenMode()}>
            {(displayProps: { fullScreenMode: boolean }) => (
                <Surface background={SurfaceBackground.neutral}>
                    <ScreenSizeObserver>
                        {(screenSizeProps: { screenSize: ScreenSize }) => {
                            const isExtraSmallWidth = screenSizeProps.screenSize === ScreenSize.xsmall;
                            if (isExtraSmallWidth) {
                                filesService.setDiffOrientation(DiffViewerOrientation.Inline);
                            }
                            return (
                                <Splitter
                                    className={css("scroll-hidden bolt-page-grey", isExtraSmallWidth && "extra-small-view")}
                                    collapsed={filesService.getExplorerCollapsed()}
                                    expandTooltip={Resources.ShowExplorerPane}
                                    farElementClassName="flex-column"
                                    fixedElement={SplitterElementPosition.Near}
                                    initialFixedSize={parseInt("" + tryGetLocalStorageValue(splitterSettingsKey)) || 300}
                                    key={"" + displayProps.fullScreenMode}
                                    minFixedSize={180}
                                    nearElementClassName="flex-column"
                                    onCollapsedChanged={collapsed => filesService.setExplorerCollapsed(collapsed)}
                                    onFixedSizeChanged={newSize => {
                                        trySetLocalStorageValue(splitterSettingsKey, "" + newSize);
                                    }}
                                    onRenderFarElement={onRenderFarElement}
                                    onRenderNearElement={isExtraSmallWidth || displayProps.fullScreenMode ? undefined : onRenderNearElement}
                                />
                            );
                        }}
                    </ScreenSizeObserver>
                </Surface>
            )}
        </Observer>
    );
};

Components.add("ms.vss-code-web.tfvc-files-hub", FilesHub);
